import { Component, signal, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AppConstants } from '../app-constants.service';
import { CommonSuperComponent } from './common.super';
import { PasswordCriteria } from './password-criteria';
import { ConfigurationApiService } from '../../generated/api/configuration/configuration.service';
import { RegistrationApiService } from '../../generated/api/registration/registration.service';
import { EnterNewPasswordModel } from '../model.module/enter-new-password.model';
import { ErrorModel, ErrorSeverity } from '../model.module/modals.model';
import { ErrorObservable } from '../services.module/message.observable';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./common.component.less'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LoginComponent extends CommonSuperComponent {

    state: undefined | 'verifyEmail' | 'verifyUserWithEmail' | 'finished';

    uuid: string;

    btnIsLocked = signal(true);

    passwordModel: EnterNewPasswordModel = new EnterNewPasswordModel();

    finishedText: string;

    pwCorrectNumChars = false;
    pwCorrectMajuscule = false;
    pwCorrectMinuscule = false;
    pwCorrectDigit = false;
    pwCorrectSpecialChar = false;
    pwCheckNoOtherChars = true;
    pwCorrectPasswordConfirm = false;

    constructor(
        protected override readonly configurationApiService: ConfigurationApiService,
        protected override readonly appConstants: AppConstants,
        private readonly activatedRoute: ActivatedRoute,
        private readonly constants: AppConstants,
        private readonly registrationApiService: RegistrationApiService,
        private readonly errorObservable: ErrorObservable
    ) {
        super(appConstants, configurationApiService);

        this.activatedRoute.queryParams.subscribe(params => {
            const uuid = params['uuid'];
            const type = params['type'];

            if (type === 'user' && uuid) {
                this.state = 'verifyUserWithEmail';
                this.uuid = uuid;
            } else if (uuid) {
                this.state = 'verifyEmail';
                this.verifyEmail(uuid);
            } else {
                // Leite um zur Keycloak-Loginseite bzw. zum Cockpit
                window.location.replace(window.location.origin);
            }
        });
    }

    verifyEmail(uuid: string) {
        this.btnIsLocked.set(true);

        console.debug('Verifiziere E-Mail-Adresse für UUID', uuid, this);

        this.registrationApiService.postVerifyRegistrationPuma({ uuid })
            .subscribe({
                next: response => {
                    console.debug('Registrierung erfolgreich vervollständigt ', response, this);

                    this.finishedText = 'Die Registrierung wurde erfolgreich abgeschlossen. ' +
                        'Bitte beachten Sie, dass es bis zu einer Minute dauern kann, bis Ihr Zugang freigeschaltet ist. ' +
                        'Klicken Sie auf den Button, um zum Login zu gelangen.';

                    this.state = 'finished';
                },
                error: (error: Response) => {
                    this.btnIsLocked.set(false);

                    this.errorObservable.notify(new ErrorModel(
                        ErrorSeverity.FATAL,
                        'Fehler bei Mailverifizierung',
                        [this.constants.messages.registrationVerifyUserNotFound],
                        error
                    ));

                    this.finishedText = 'Klicken Sie auf den Button, um zurück zum Login zu gelangen.';
                    this.state = 'finished';
                }
            });
    }

    verifyEmailWithPassword() {
        this.btnIsLocked.set(true);

        this.registrationApiService.postVerifyUser({
            uuid: this.uuid,
            password: this.passwordModel.password.value
        }).subscribe({
            next: res => {
                if (res.isSuccess) {
                    this.passwordModel = null;

                    this.finishedText = 'Nutzer erfolgreich angelegt. '
                        + 'Sie erhalten Ihren Benutzernamen in Kürze per E-Mail und können Ihren Account dann verwenden. '
                        + 'Klicken Sie auf den Button, um zum Login zu gelangen.';

                    this.state = 'finished';
                } else {
                    // FIXME
                    //this.passwordModel.mapResponse(res.password);
                }
            },
            error: (error: Response) => {
                this.btnIsLocked.set(false);

                this.errorObservable.notify(new ErrorModel(
                    ErrorSeverity.FATAL,
                    'Fehler bei Mailverifizierung',
                    [this.constants.messages.registrationVerifyUserNotFound],
                    error
                ));

                this.finishedText = 'Klicken Sie auf den Button, um zurück zum Login zu gelangen.';
                this.state = 'finished';
            }
        });
    }

    validatePassword(): void {
        if (this.passwordModel.passwordConfirm) {
            this.passwordModel.validateField(this.passwordModel.password);
        }
    }

    handlePasswortInput(): void {
        const validationResult = this.passwordModel.validate();
        const valid = validationResult === null;

        if (!valid) {
            return;
        }

        this.verifyEmailWithPassword();
    }

    onNewPasswordKeyUp(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            this.handlePasswortInput();
        }

        this.updatePwChecklist();
    }

    handleLoginBtnClick() {
        window.location.replace(window.location.origin);
    }

    private updatePwChecklist(): void {
        const newValue = this.passwordModel.password.value;
        const confirmValue = this.passwordModel.passwordConfirm;

        const numChars = newValue.length;
        this.pwCorrectNumChars = numChars >= PasswordCriteria.PW_MIN_LENGTH && numChars <= PasswordCriteria.PW_MAX_LENGTH;
        this.pwCorrectMajuscule = PasswordCriteria.PW_CRITERIA_MAJUSCULE.test(newValue);
        this.pwCorrectMinuscule = PasswordCriteria.PW_CRITERIA_MINUSCULE.test(newValue);
        this.pwCorrectDigit = PasswordCriteria.PW_CRITERIA_DIGIT.test(newValue);
        this.pwCorrectSpecialChar = PasswordCriteria.PW_CRITERIA_SPECIAL_CHAR.test(newValue);
        this.pwCheckNoOtherChars = PasswordCriteria.PW_CRITERIA_NO_OTHER_CHARS.test(newValue);
        this.pwCorrectPasswordConfirm = newValue && newValue === confirmValue;

        const btnShallBeLocked = !(
            this.pwCorrectNumChars
            && this.pwCorrectMajuscule
            && this.pwCorrectMinuscule
            && this.pwCorrectDigit
            && this.pwCorrectSpecialChar
            && this.pwCheckNoOtherChars
            && this.pwCorrectPasswordConfirm
        );

        this.btnIsLocked.set(btnShallBeLocked);
    }

}
